define(function(require, exports, module) {
    'use strict';

    var $ = require('j');
    var _ = require('underscore');
    var core = require('core');
    var historyUtil = require('util/history.util');
    var device = require('util/device.util');
    var urlUtil = require('util/url.util');
    var util = require('utils');
    var i18n = require('i18n');
    var server = require('server');
    var storage = require('util/storage.util');

    module.exports = {

        /**
         * @typedef {Object} PekoResponse
         * @property {String} ID
         *
         * @property {Object} data
         *
         * @property {Object} data.error
         * @property {String} data.error.code
         * @property {String} data.error.description
         * @property {String} data.error.message
         *
         * @property {Object} description
         */

        /**
         * @typedef {Object} NonPekoResponse
         * @property {String} ID
         *
         * @property {Object} data
         *
         * @property {Object} data.error
         * @property {String} data.error.code
         * @property {String} data.error.description
         * @property {String} data.error.message
         * @property {String} data.error.originally
         * @property {String} data.error.severity
         *
         * @property {Number} data.errorCode
         * @property {String} data.errorDescription
         * @property {String} data.errorMessage
         *
         * @property {Object} description
         */

        /**
         * Check if response if from PEKO-enabled server.
         * While we intend to drop non-PEKO support in the future, all fallbacks are treated as PEKO enabled.
         * @param {PekoResponse|NonPekoResponse} response
         * @returns {boolean}
         */

        isPekoResponse: function(response) {
            var error = util.fromPath(response, 'data.error');
            return error && !error.originally;
        },

        _getPekoErrorMessage: function(response) {
            var error = util.fromPath(response, 'data.error') || {};
            var translationKey = 'playtech.mobile.system.mobile-cashier.error.'
                + (error.code != null ? error.code : 404);
            var fallbackMessage = error.message || error.description;

            return i18n.tExt(translationKey, fallbackMessage);
        },

        /**
         * Legacy method to get error message when we retrieve method and account lists (GPML+GPMA)
         * @param response
         * @param cashierType
         * @returns {*}
         */
        getNonPekoErrorMessageGpm: function(response = {}, cashierType) {
            if (response.ID === -1) {
                return response.data?.message;
            }

            return i18n.tExt('playtech.mobile.system.mobile-cashier.error-' + cashierType + '.' +
                (response.data?.errorCode != null ? response.data?.errorCode : 404),
                response.data?.errorMessage || response.data?.errorDescription || response.data?.error?.description
            );
        },

        /**
         * Try to generate correct error message from response
         * @param response
         * @returns {String}
         */
        getErrorMessage: function(response) {
            if (!this.isPekoResponse(response)) {
                return null;
            }

            return this._getPekoErrorMessage(response);
        },

        notifyTransactionStatus: function(href) {
            var deferred = $.Deferred();
            var urlParams = urlUtil.getURLParams(href);

            if (urlParams.initiator !== 'cashier_backbone') {
                return deferred.resolve();
            }

            var status = urlParams.status;
            var action = urlParams.action;
            var data = urlParams.extraData;
            var transactionCode = urlParams.transactionCode || '';
            var title = action ? i18n.t('playtech.mobile.system.mobile-cashier.' + action + '.title') : '';
            // must take URL params from location.href, and not from href here
            var search = urlUtil.serialize(_.omit(urlUtil.getURLParams(), ['initiator', 'transactionCode', 'status', 'action', 'redirectType', 'extraData', 'type', 'errorCode', 'amount']));
            var resultUrl = urlUtil.pathname + `${search ? `?${search}` : ''}` + urlUtil.hash;
            var result = {
                status: status,
                action: action,
                title: title,
                data: data
            };
            var eventName;

            this.promoCode.handleTransactionResult(result);

            this._handleIosSafariScalingBug();

            if (_.contains(['success', 'error', 'cancel', 'waiting'], status)) {
                window.Playtech.API.common.navigate(resultUrl, {
                    ...historyUtil.state,
                    replace: true
                }, { preserveSearchParams: false });

                /**
                 * Possible values:
                 * 'deposit:success', 'deposit:fail', 'withdraw:success', 'withdraw:fail'
                 * @type {string}
                 */
                eventName = action + ':' + (status === 'success' ? 'success' : 'fail');

                core.trigger(eventName, {
                    amount: urlParams.amount,
                    status: urlParams.status,
                    code: urlParams.transactionCode,
                    method: urlParams.type
                });

                // will show popup on DC side
                if (device.isDownloadClient()) {
                    return deferred.resolve();
                }

                if (transactionCode.length) {
                    return server.send({
                            ID: 35640,
                            description: 'Get Transaction Player Message',
                            transactionId: transactionCode,
                            type: action,
                            responses: [35641, 35645]
                        })
                        .done(function(response) {
                            _.map(response.data.actions, function(responseAction) {
                                this.showNotifyTransactionStatusPopup(result,
                                    responseAction.showMessageNotificationInfo.message);
                            }.bind(this));
                        }.bind(this))
                        .fail(function() {
                            this.showNotifyTransactionStatusPopup(result);
                        }.bind(this));
                }

                this.showNotifyTransactionStatusPopup(result);

                return deferred.resolve();
            }

            return deferred.resolve();
        },

        showNotifyTransactionStatusPopup: function(result, content) {
            window.Playtech.API.popup.showPopup({
                title: result.title,
                content: content || i18n.t('playtech.mobile.system.mobile-cashier.transaction.' + result.status),
                onClose: this.onCloseNotifyTransactionStatusPopup.bind(this, result)
            });
        },

        onCloseNotifyTransactionStatusPopup: function(result) {
            // Send notification about success deposit
            if (result.status === 'success' && result.action === 'deposit') {
                core.trigger('app:message', {
                    ID: 'successDeposit',
                    data: {
                        message: 'Success deposit notification'
                    }
                });
            }
        },

        _handleIosSafariScalingBug: function() {
            var url = new URL(window.location);
            var isDotpayCard = url.searchParams.get('type') === 'DotpayCard';
            var isIos = device.os === 'ios';
            var isSafari = device.browserName === 'safari';

            if (isDotpayCard && isIos && isSafari) {
                var meta = document.head.querySelector('meta#viewport');
                var contentBackup = meta.content;

                meta.content = meta.content.replace(/scale=[\d.]+/g, 'scale=1.0');
                meta.content = contentBackup;
            }
        },

        promoCode: {
            lsKeyName: 'promocode',

            getFromStorage: function() {
                return storage.local.getItem(this.lsKeyName);
            },

            removeFromStorage: function() {
                storage.local.removeItem(this.lsKeyName);
                core.trigger('storage:change:promoCode', null);
            },

            handleTransactionResult: function(result) {
                var status = result.status;
                var action = result.action;
                var data = result.data;
                var storedPromoCode = this.getFromStorage();

                if (
                    data
                    && storedPromoCode
                    && action === 'deposit'
                    && status === 'success'
                ) {
                    var sentPromoCode;

                    try {
                        data = JSON.parse(window.atob(data));
                        sentPromoCode = data.promocode;
                    } catch (e) {
                        console.warn('Cannot parse transaction data.');
                        return;
                    }

                    if (sentPromoCode === storedPromoCode) {
                        this.removeFromStorage();
                    }
                }

            }
        }
    };
});
